import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import { getLocationTypeLabel } from "src/modules/3rd-party-management/constants/Common";
import { formatDisplayValue } from "../../helpers";

const formatDate = (dateUtils, value) => {
    if (!value) {
        return "-";
    }

    return dateUtils.convertDateToDate(value, INTERNATIONAL_DATE_FORMAT);
};

const toYesNo = (value) => {
    if (value === true) {
        return "Yes";
    }
    if (value === false) {
        return "No";
    }
    return "-";
};

const resolveShareholderName = (shareholder = {}) => {
    if (shareholder?.companyName) {
        return shareholder.companyName;
    }

    return [
        shareholder?.title,
        shareholder?.firstName,
        shareholder?.lastName,
    ].filter(Boolean).join(" ");
};

const resolveShareholderType = (shareholder = {}) => {
    if (shareholder?.companyName) {
        return "Company";
    }

    if (shareholder?.firstName || shareholder?.lastName) {
        return "Individual";
    }

    return "-";
};

export const mapRows = (rows) =>
    rows.map((row) => ({
        ...row,
        details: formatDisplayValue(row.details),
    }));

const joinParts = (parts = [], separator = ", ") =>
    parts.filter((part) => part != null && String(part).trim() !== "").join(separator);

const formatFullAddress = (location = {}) =>
    joinParts([
        location?.address,
        location?.postalCode,
        location?.city,
        location?.regionName || location?.state,
        location?.regionCode,
        location?.regionShort,
    ]);

const formatCountry = (location = {}) =>
    joinParts([
        location?.countryName,
        location?.countryCode ? `(${location.countryCode})` : null,
    ], " ");

const formatContactName = (contact = {}) =>
    joinParts([contact?.title, contact?.name, contact?.lastName], " ");

const formatContactPhone = (contact = {}) =>
    joinParts([
        contact?.countryCallingCode,
        contact?.phoneNumber,
    ], " ");

const formatManagerName = (manager = {}) =>
    joinParts([
        manager?.title,
        manager?.fullName || manager?.name,
        manager?.lastName,
    ], " ");

const formatShareholderLocation = (shareholder = {}) =>
    joinParts([
        shareholder?.country?.name || shareholder?.country,
        shareholder?.region,
        shareholder?.city,
        shareholder?.residenceCountry?.name,
    ]);

export const LOCATION_COLUMNS = [
    { key: "index", label: "#", width: "6%" },
    { key: "type", label: "Type", width: "14%" },
    { key: "address", label: "Address", width: "42%" },
    { key: "country", label: "Country", width: "20%" },
    { key: "deliverability", label: "Deliverability", width: "18%" },
];

export const CONTACT_COLUMNS = [
    { key: "index", label: "#", width: "6%" },
    { key: "name", label: "Name", width: "22%" },
    { key: "position", label: "Position", width: "18%" },
    { key: "email", label: "Email", width: "24%" },
    { key: "phone", label: "Phone", width: "16%" },
    { key: "fax", label: "Fax", width: "14%" },
];

export const SHAREHOLDER_COLUMNS = [
    { key: "index", label: "#", width: "5%" },
    { key: "name", label: "Name", width: "20%" },
    { key: "type", label: "Type", width: "10%" },
    { key: "percentage", label: "Share %", width: "9%" },
    { key: "ubo", label: "UBO", width: "7%" },
    { key: "address", label: "Address", width: "20%" },
    { key: "location", label: "Location", width: "17%" },
    { key: "dunsNumber", label: "DUNS", width: "12%" },
];

export const MANAGEMENT_COLUMNS = [
    { key: "index", label: "#", width: "5%" },
    { key: "name", label: "Name", width: "17%" },
    { key: "position", label: "Position", width: "13%" },
    { key: "email", label: "Email", width: "17%" },
    { key: "phone", label: "Phone", width: "11%" },
    { key: "sanctionStatus", label: "Sanction", width: "10%" },
    { key: "pepStatus", label: "PEPs", width: "9%" },
    { key: "active", label: "Active", width: "7%" },
    { key: "appointmentDate", label: "Appointed", width: "11%" },
];

export const buildLocationRecords = (locations = []) => {
    if (!Array.isArray(locations)) {
        return [];
    }

    return locations.map((location, index) => ({
        id: location?.id || `location-${index}`,
        index: index + 1,
        type: getLocationTypeLabel(location?.type),
        address: formatFullAddress(location),
        country: formatCountry(location),
        deliverability: location?.deliverabilityStatus,
    }));
};

export const buildContactRecords = (contactPersons = []) => {
    if (!Array.isArray(contactPersons)) {
        return [];
    }

    return contactPersons.map((contact, index) => ({
        id: contact?.id || `contact-${index}`,
        index: index + 1,
        name: formatContactName(contact),
        position: contact?.position,
        email: contact?.email,
        phone: formatContactPhone(contact),
        fax: contact?.faxNumber,
    }));
};

export const buildShareholderRecords = (shareHolders = {}) => {
    const companyShareholders = Array.isArray(shareHolders?.company) ? shareHolders.company : [];
    const individualShareholders = Array.isArray(shareHolders?.individual) ? shareHolders.individual : [];
    const allShareholders = [
        ...companyShareholders.map((shareholder) => ({ ...shareholder, shareholderType: "Company" })),
        ...individualShareholders.map((shareholder) => ({ ...shareholder, shareholderType: "Individual" })),
    ];

    return allShareholders.map((shareholder, index) => ({
        id: shareholder?.id || `shareholder-${index}`,
        index: index + 1,
        name: resolveShareholderName(shareholder),
        type: shareholder?.shareholderType || resolveShareholderType(shareholder),
        percentage: shareholder?.percentage,
        ubo: toYesNo(shareholder?.isUbo),
        address: shareholder?.address || shareholder?.shareholderAddress,
        location: formatShareholderLocation(shareholder),
        dunsNumber: shareholder?.dunsNumber,
    }));
};

export const buildManagementRecords = (teamManagement = [], dateUtils) => {
    if (!Array.isArray(teamManagement)) {
        return [];
    }

    return teamManagement.map((manager, index) => ({
        id: manager?.id || `manager-${index}`,
        index: index + 1,
        name: formatManagerName(manager),
        position: manager?.position,
        email: manager?.email,
        phone: manager?.phoneNumber,
        sanctionStatus: manager?.sanctionStatus,
        pepStatus: manager?.pepStatus || manager?.pepsStatus,
        active: toYesNo(manager?.active ?? manager?.isActive),
        appointmentDate: formatDate(dateUtils, manager?.appointmentDate || manager?.createdAt),
    }));
};

export const buildBasicInfoRows = (supplierDetail = {}) => {
    const industries = Array.isArray(supplierDetail?.industries)
        ? supplierDetail.industries
            .map((industry) => [industry?.class, industry?.name].filter(Boolean).join(" - "))
            .filter(Boolean)
            .join(", ")
        : "-";

    return [
        { title: "Company Name", details: supplierDetail?.name },
        {
            title: "Former Company Name",
            details: supplierDetail?.formerCompanyName || supplierDetail?.localLanguageFullName,
        },
        { title: "DUNS Number", details: supplierDetail?.dunsNumber },
        {
            title: "Business Number",
            details: supplierDetail?.businessNumber || supplierDetail?.registrationNumber,
        },
        { title: "Business Number Type", details: supplierDetail?.businessNumberType },
        { title: "Legal Form", details: supplierDetail?.legalForm },
        { title: "Line of Business", details: supplierDetail?.lineOfBusiness || supplierDetail?.subType },
        { title: "Location Status", details: supplierDetail?.locationStatus },
        { title: "Industries", details: industries },
        { title: "Brands", details: supplierDetail?.brands },
    ];
};

export const buildFinancialRows = (supplierDetail = {}) => [
    { title: "SIC Code", details: supplierDetail?.sicCode },
    { title: "Local Activity Code", details: supplierDetail?.localActivityCode || supplierDetail?.activityCode },
    { title: "Statement Date", details: supplierDetail?.statementDate },
    { title: "Annual Sales", details: supplierDetail?.annualSales },
    { title: "Annual Sales (USD)", details: supplierDetail?.annualSalesUsd },
    { title: "Net Worth", details: supplierDetail?.netWorth },
    { title: "Net Worth (USD)", details: supplierDetail?.netWorthUsd },
    { title: "Net Income", details: supplierDetail?.netIncome },
    { title: "Employees Count", details: supplierDetail?.employeesCount },
    { title: "Out of Business", details: toYesNo(supplierDetail?.outOfBusiness) },
];

export const buildMetadataRows = (supplierDetail = {}, dateUtils) => [
    { title: "Source", details: supplierDetail?.source },
    { title: "Created At", details: formatDate(dateUtils, supplierDetail?.creationDate) },
    {
        title: "Updated At",
        details: formatDate(dateUtils, supplierDetail?.lastUpdate || supplierDetail?.updateDate),
    },
];
